// filepath: apps/web/src/features/promote/PromotionDiffPage.tsx
//
// FR-UI-PROM-1: page component that composes diff view + irreversibility
// warning + confirmation checkbox + approve button.
//
// Composes: DiffView, IrreversibilityWarning (conditional), IrreversibilityCheckbox,
// and the approve mutation. Shows loading and error states.

import React, { useState } from "react";
import { usePromotionReview } from "./usePromotionReview";
import { useApprovePromotion } from "./useApprovePromotion";
import { DiffView } from "./DiffView";
import { IrreversibilityCheckbox } from "./IrreversibilityCheckbox";
import type { DefinitionDiff } from "./types";

interface PromotionDiffPageProps {
  reviewId: string;
}

export function PromotionDiffPage({
  reviewId,
}: PromotionDiffPageProps): React.ReactElement {
  const { data, isLoading, error } = usePromotionReview(reviewId);
  const approveMutation = useApprovePromotion();
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!data) {
    return <p className="text-muted-foreground">Review not found.</p>;
  }

  const { review, diff_report } = data;
  const diffs: DefinitionDiff[] = diff_report ? [diff_report] : [];

  // Determine if the plan has irreversible contract steps.
  const hasIrreversible = diffs.some(
    (d) => d.irreversible && d.contract_steps && d.contract_steps.length > 0,
  );

  // The checkbox and confirmIrreversible flag are only required when
  // the plan has irreversible steps.
  const requiresCheckbox = hasIrreversible;
  const canApprove = !requiresCheckbox || checkboxChecked;

  async function handleApprove() {
    if (!canApprove) return;
    await approveMutation.mutateAsync({
      reviewId,
      confirmIrreversible: requiresCheckbox,
    });
  }

  return (
    <div className="space-y-6" data-testid="promotion-diff-page">
      {/* Review status banner */}
      <ReviewStatusBanner status={review.status} />

      {/* Diff view */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Changes</h2>
        {diffs.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No diff available for this review.
          </p>
        ) : (
          <DiffView diffs={diffs} />
        )}
      </section>

      {/* Irreversibility confirmation — only shown when plan has contract steps */}
      {requiresCheckbox && (
        <section>
          <IrreversibilityCheckbox
            checked={checkboxChecked}
            onChange={setCheckboxChecked}
          />
        </section>
      )}

      {/* Approve action */}
      {review.status === "pending_review" && (
        <section>
          <button
            type="button"
            onClick={handleApprove}
            disabled={!canApprove || approveMutation.isPending}
            className={`
              px-4 py-2 rounded text-sm font-medium transition-colors
              ${
                canApprove && !approveMutation.isPending
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              }
            `}
            data-testid="approve-button"
          >
            {approveMutation.isPending ? "Approving…" : "Approve Promotion"}
          </button>
          {approveMutation.isError && (
            <p className="mt-2 text-sm text-destructive" role="alert">
              Approval failed:{" "}
              {(approveMutation.error as { message: string }).message}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function ReviewStatusBanner({
  status,
}: {
  status: string;
}): React.ReactElement {
  const colors: Record<string, string> = {
    pending_review: "bg-yellow-50 border-yellow-200 text-yellow-800",
    approved: "bg-green-50 border-green-200 text-green-800",
    rejected: "bg-red-50 border-red-200 text-red-800",
    applied: "bg-blue-50 border-blue-200 text-blue-800",
    failed: "bg-red-50 border-red-200 text-red-800",
    superseded: "bg-gray-50 border-gray-200 text-gray-800",
  };
  const cls = colors[status] ?? "bg-gray-50 border-gray-200 text-gray-800";
  return (
    <div className={`rounded border px-3 py-2 text-sm ${cls}`} role="status">
      Status:{" "}
      <span className="font-medium capitalize">{status.replace("_", " ")}</span>
    </div>
  );
}

function LoadingSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-12 bg-muted rounded" />
      <div className="h-32 bg-muted rounded" />
      <div className="h-8 bg-muted rounded" />
    </div>
  );
}

function ErrorState({ error }: { error: Error }): React.ReactElement {
  return (
    <div className="text-destructive text-sm" role="alert">
      Failed to load review: {error.message}
    </div>
  );
}
